// src/types.ts
var DServerError = class extends Error {
  constructor(message, status, statusText, body) {
    super(message);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.name = "DServerError";
  }
};
var AuthenticationError = class extends DServerError {
  constructor(message = "Authentication failed", body) {
    super(message, 401, "Unauthorized", body);
    this.name = "AuthenticationError";
  }
};
var AuthorizationError = class extends DServerError {
  constructor(message = "Access denied", body) {
    super(message, 403, "Forbidden", body);
    this.name = "AuthorizationError";
  }
};
var NotFoundError = class extends DServerError {
  constructor(message = "Resource not found", body) {
    super(message, 404, "Not Found", body);
    this.name = "NotFoundError";
  }
};

// src/utils.ts
function parsePaginationHeader(headerValue, requestedPageSize, dataLength) {
  let raw = {};
  if (headerValue) {
    try {
      raw = JSON.parse(headerValue);
    } catch {
    }
  }
  const total = typeof raw.total === "number" ? raw.total : dataLength;
  const page = typeof raw.page === "number" ? raw.page : 1;
  const per_page = typeof raw.per_page === "number" ? raw.per_page : requestedPageSize ?? dataLength;
  let pages;
  if (typeof raw.pages === "number") {
    pages = raw.pages;
  } else if (typeof raw.total_pages === "number") {
    pages = raw.total_pages;
  } else {
    pages = per_page > 0 ? Math.ceil(total / per_page) : 1;
  }
  return { total, page, per_page, pages };
}
async function generateIdentifier(relpath) {
  const encoder = new TextEncoder();
  const data = encoder.encode(relpath);
  const hashBuffer = await crypto.subtle.digest("SHA-1", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function encodeUri(uri) {
  return encodeURIComponent(uri);
}
function getJwtSubject(token) {
  const parts = token.split(".");
  if (parts.length !== 3) {
    return void 0;
  }
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return typeof payload.sub === "string" ? payload.sub : void 0;
  } catch {
    return void 0;
  }
}
function getCurrentTimestamp() {
  return Math.floor(Date.now() / 1e3);
}
function parseISOTimestamp(isoString) {
  return Math.floor(new Date(isoString).getTime() / 1e3);
}
function isExpired(expiryTimestamp) {
  const expiry = parseISOTimestamp(expiryTimestamp);
  const now = getCurrentTimestamp();
  return now >= expiry;
}
function timeUntilExpiry(expiryTimestamp) {
  const expiry = parseISOTimestamp(expiryTimestamp);
  const now = getCurrentTimestamp();
  return Math.max(0, expiry - now);
}
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function withRetry(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1e3,
    maxDelay = 3e4,
    backoffFactor = 2
  } = options;
  let lastError;
  let currentDelay = initialDelay;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const status = error.status;
      if (lastError.name === "AbortError" || typeof status === "number" && status >= 400 && status < 500) {
        throw lastError;
      }
      if (attempt < maxRetries) {
        await delay(currentDelay);
        currentDelay = Math.min(currentDelay * backoffFactor, maxDelay);
      }
    }
  }
  throw lastError;
}
function chunk(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}
async function parallelLimit(items, limit, fn) {
  const results = [];
  const executing = /* @__PURE__ */ new Set();
  for (const item of items) {
    const p = Promise.resolve().then(() => fn(item));
    results.push(p);
    const e = p.then(
      () => void 0,
      () => void 0
    ).then(() => {
      executing.delete(e);
    });
    executing.add(e);
    if (executing.size >= limit) {
      await Promise.race(executing);
    }
  }
  const settled = await Promise.allSettled(results);
  const firstRejected = settled.find((s) => s.status === "rejected");
  if (firstRejected) {
    throw firstRejected.reason;
  }
  return settled.map((s) => s.value);
}

// src/client.ts
import SparkMD5 from "spark-md5";
function extractErrorMessage(errorBody) {
  if (typeof errorBody === "string" && errorBody.trim()) {
    return errorBody;
  }
  if (errorBody && typeof errorBody === "object") {
    const body = errorBody;
    for (const key of ["message", "error", "description", "msg"]) {
      if (typeof body[key] === "string" && body[key].trim()) {
        return body[key];
      }
    }
  }
  return void 0;
}
var DServerClient = class {
  constructor(config) {
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this.token = config.token;
    this.fetchImpl = config.fetch ?? fetch.bind(globalThis);
  }
  /**
   * Set or update the authentication token
   */
  setToken(token) {
    this.token = token;
  }
  /**
   * Get authorization headers
   */
  getHeaders() {
    const headers = {
      "Content-Type": "application/json"
    };
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    return headers;
  }
  /**
   * Make an authenticated request to dserver
   */
  async request(method, path, body, signal) {
    const url = `${this.baseUrl}${path}`;
    const options = {
      method,
      headers: this.getHeaders(),
      signal
    };
    if (body !== void 0) {
      options.body = JSON.stringify(body);
    }
    const response = await this.fetchImpl(url, options);
    if (!response.ok) {
      return this.handleErrorResponse(response);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : void 0;
  }
  // =========================================================================
  // Signed URL API
  // =========================================================================
  /**
   * Get signed URLs for reading an entire dataset
   *
   * @param uri - Dataset URI (e.g., "s3://bucket/uuid")
   * @returns Signed URLs for all dataset components
   */
  async getDatasetSignedUrls(uri) {
    const encodedUri = encodeUri(uri);
    return this.request(
      "GET",
      `/signed-urls/dataset/${encodedUri}`
    );
  }
  /**
   * Get a signed URL for a single dataset item
   *
   * @param uri - Dataset URI
   * @param identifier - Item identifier (SHA-1 hash of relpath)
   * @returns Signed URL for the item
   */
  async getItemSignedUrl(uri, identifier) {
    const encodedUri = encodeUri(uri);
    return this.request(
      "GET",
      `/signed-urls/item/${encodedUri}/${identifier}`
    );
  }
  /**
   * Get signed URLs for uploading a new dataset
   *
   * @param baseUri - Base URI (e.g., "s3://bucket")
   * @param request - Upload request with UUID, name, and items
   * @returns Signed URLs for uploading dataset components
   */
  async getUploadUrls(baseUri, request) {
    const encodedBaseUri = encodeUri(baseUri);
    return this.request(
      "POST",
      `/signed-urls/upload/${encodedBaseUri}`,
      request
    );
  }
  /**
   * Signal that a dataset upload is complete
   *
   * @param uri - Dataset URI
   * @returns Registration result
   */
  async signalUploadComplete(uri) {
    const request = { uri };
    return this.request(
      "POST",
      `/signed-urls/upload-complete`,
      request
    );
  }
  // =========================================================================
  // High-level download operations
  // =========================================================================
  /**
   * Download a dataset item using its signed URL
   *
   * @param urls - Signed URLs response from getDatasetSignedUrls
   * @param identifier - Item identifier
   * @param options - Download options
   * @returns Item content as ArrayBuffer
   */
  async downloadItem(urls, identifier, options = {}) {
    const itemUrl = urls.item_urls[identifier];
    if (!itemUrl) {
      throw new NotFoundError(`Item ${identifier} not found in dataset`);
    }
    const response = await this.fetchImpl(itemUrl, { signal: options.signal });
    if (!response.ok) {
      throw new DServerError(
        `Failed to download item: ${response.statusText}`,
        response.status
      );
    }
    if (options.onProgress && response.body) {
      const reader = response.body.getReader();
      const contentLength = Number(response.headers.get("Content-Length")) || 0;
      const chunks = [];
      let receivedLength = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        options.onProgress(receivedLength, contentLength, identifier);
      }
      const result = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk2 of chunks) {
        result.set(chunk2, position);
        position += chunk2.length;
      }
      return result.buffer;
    }
    return response.arrayBuffer();
  }
  /**
   * Download and parse the dataset manifest
   *
   * @param urls - Signed URLs response
   * @returns Parsed manifest
   */
  async downloadManifest(urls) {
    const response = await this.fetchImpl(urls.manifest_url);
    if (!response.ok) {
      throw new DServerError("Failed to download manifest", response.status);
    }
    return response.json();
  }
  /**
   * Download and parse the dataset admin metadata
   *
   * @param urls - Signed URLs response
   * @returns Parsed admin metadata
   */
  async downloadAdminMetadata(urls) {
    const response = await this.fetchImpl(urls.admin_metadata_url);
    if (!response.ok) {
      throw new DServerError(
        "Failed to download admin metadata",
        response.status
      );
    }
    return response.json();
  }
  /**
   * Download the dataset README
   *
   * @param urls - Signed URLs response
   * @returns README content as string
   */
  async downloadReadme(urls) {
    const response = await this.fetchImpl(urls.readme_url);
    if (!response.ok) {
      throw new DServerError("Failed to download README", response.status);
    }
    return response.text();
  }
  /**
   * Download an overlay
   *
   * @param urls - Signed URLs response
   * @param overlayName - Name of the overlay
   * @returns Overlay data
   */
  async downloadOverlay(urls, overlayName) {
    const overlayUrl = urls.overlay_urls[overlayName];
    if (!overlayUrl) {
      throw new NotFoundError(`Overlay ${overlayName} not found`);
    }
    const response = await this.fetchImpl(overlayUrl);
    if (!response.ok) {
      throw new DServerError("Failed to download overlay", response.status);
    }
    return response.json();
  }
  /**
   * Download an annotation
   *
   * @param urls - Signed URLs response
   * @param annotationName - Name of the annotation
   * @returns Annotation value
   */
  async downloadAnnotation(urls, annotationName) {
    const annotationUrl = urls.annotation_urls[annotationName];
    if (!annotationUrl) {
      throw new NotFoundError(`Annotation ${annotationName} not found`);
    }
    const response = await this.fetchImpl(annotationUrl);
    if (!response.ok) {
      throw new DServerError("Failed to download annotation", response.status);
    }
    return response.json();
  }
  // =========================================================================
  // High-level upload operations
  // =========================================================================
  /**
   * Create and upload a new dataset
   *
   * The server handles admin_metadata, manifest, structure, tags, and annotations
   * directly based on metadata sent in the upload request. We only need to upload
   * README and item files.
   *
   * @param baseUri - Base URI (e.g., "s3://bucket")
   * @param name - Dataset name
   * @param files - Files to upload
   * @param options - Upload options (readme, tags, annotations)
   * @returns Upload completion response
   */
  async createDataset(baseUri, name, files, options = {}) {
    const uuid = generateUUID();
    const creatorUsername = this.token && getJwtSubject(this.token) || "webapp-user";
    const frozenAt = getCurrentTimestamp();
    const itemsWithMetadata = await Promise.all(
      files.map(async (file) => {
        const identifier = await generateIdentifier(file.relpath);
        const { size, hash } = await this.computeItemMetadata(file);
        return {
          ...file,
          identifier,
          size_in_bytes: size,
          hash,
          utc_timestamp: frozenAt
        };
      })
    );
    const uploadInfo = await this.getUploadUrls(baseUri, {
      uuid,
      name,
      creator_username: creatorUsername,
      frozen_at: frozenAt,
      items: itemsWithMetadata.map((item) => ({
        relpath: item.relpath,
        size_in_bytes: item.size_in_bytes,
        hash: item.hash,
        utc_timestamp: item.utc_timestamp
      })),
      tags: options.tags,
      annotations: options.annotations
    });
    const totalBytes = itemsWithMetadata.reduce(
      (sum, item) => sum + item.size_in_bytes,
      0
    );
    let uploadedBytes = 0;
    const readme = options.readme || "---\n";
    await this.uploadText(uploadInfo.upload_urls.readme, readme, options.signal);
    await parallelLimit(itemsWithMetadata, 4, async (item) => {
      const uploadUrl = uploadInfo.upload_urls.items[item.identifier];
      if (!uploadUrl) {
        throw new DServerError(
          `No upload URL for item ${item.relpath}`,
          500
        );
      }
      let body;
      if (item.content instanceof Blob) {
        body = item.content;
      } else if (item.content instanceof ArrayBuffer) {
        body = item.content;
      } else {
        body = item.content;
      }
      const response = await this.fetchImpl(uploadUrl.url, {
        method: "PUT",
        body,
        headers: item.contentType ? { "Content-Type": item.contentType } : void 0,
        signal: options.signal
      });
      if (!response.ok) {
        throw new DServerError(
          `Failed to upload ${item.relpath}`,
          response.status
        );
      }
      uploadedBytes += item.size_in_bytes;
      options.onProgress?.(uploadedBytes, totalBytes, item.relpath);
    });
    return this.signalUploadComplete(uploadInfo.uri);
  }
  /**
   * Compute size and hash for a file item
   */
  async computeItemMetadata(item) {
    let size;
    let contentBuffer;
    if (item.content instanceof Blob) {
      size = item.content.size;
      contentBuffer = await item.content.arrayBuffer();
    } else if (item.content instanceof ArrayBuffer) {
      size = item.content.byteLength;
      contentBuffer = item.content;
    } else {
      const encoded = new TextEncoder().encode(item.content);
      size = encoded.length;
      contentBuffer = encoded.buffer;
    }
    const hash = SparkMD5.ArrayBuffer.hash(contentBuffer);
    return { size, hash };
  }
  /**
   * Upload text data to a signed URL
   */
  async uploadText(url, text, signal) {
    const response = await this.fetchImpl(url, {
      method: "PUT",
      body: text,
      headers: { "Content-Type": "text/plain; charset=utf-8" },
      signal
    });
    if (!response.ok) {
      throw new DServerError("Failed to upload text", response.status);
    }
  }
  // =========================================================================
  // REST API Methods (standard dserver endpoints)
  // =========================================================================
  /**
   * Get server configuration versions
   */
  async getServerVersions() {
    const response = await this.request(
      "GET",
      "/config/versions"
    );
    return response.versions;
  }
  /**
   * Check server health (no auth required)
   */
  async checkHealth() {
    const url = `${this.baseUrl}/config/health`;
    const response = await this.fetchImpl(url);
    if (!response.ok) {
      throw new DServerError("Health check failed", response.status);
    }
    return response.json();
  }
  /**
   * Get user summary info
   */
  async getUserSummary(username) {
    return this.request("GET", `/users/${username}/summary`);
  }
  /**
   * Search datasets with pagination
   */
  async searchDatasets(query, pagination) {
    const params = new URLSearchParams();
    if (pagination?.page) params.set("page", String(pagination.page));
    if (pagination?.page_size) params.set("page_size", String(pagination.page_size));
    if (pagination?.sort) params.set("sort", pagination.sort);
    const queryString = params.toString();
    const path = `/uris${queryString ? `?${queryString}` : ""}`;
    const url = `${this.baseUrl}${path}`;
    const response = await this.fetchImpl(url, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(query)
    });
    if (!response.ok) {
      await this.handleErrorResponse(response);
    }
    const data = await response.json();
    const paginationInfo = parsePaginationHeader(
      response.headers.get("x-pagination"),
      pagination?.page_size,
      data.length
    );
    return { data, pagination: paginationInfo };
  }
  /**
   * Read the error body and throw a typed error carrying the server's
   * message so callers can surface the actual reason for the failure.
   */
  async handleErrorResponse(response) {
    const text = await response.text();
    let errorBody = text;
    try {
      errorBody = JSON.parse(text);
    } catch {
    }
    const serverMessage = extractErrorMessage(errorBody);
    switch (response.status) {
      case 401:
        throw new AuthenticationError(serverMessage, errorBody);
      case 403:
        throw new AuthorizationError(serverMessage, errorBody);
      case 404:
        throw new NotFoundError(serverMessage, errorBody);
      default:
        throw new DServerError(
          serverMessage ?? `Request failed: ${response.statusText}`,
          response.status,
          response.statusText,
          errorBody
        );
    }
  }
  /**
   * Get manifest for a dataset
   */
  async getManifest(uri) {
    const encodedUri = encodeURIComponent(uri);
    return this.request("GET", `/manifests/${encodedUri}`);
  }
  /**
   * Get readme for a dataset
   */
  async getReadme(uri) {
    const encodedUri = encodeURIComponent(uri);
    return this.request("GET", `/readmes/${encodedUri}`);
  }
  /**
   * Set readme for a dataset
   */
  async setReadme(uri, content) {
    const encodedUri = encodeURIComponent(uri);
    return this.request("PUT", `/readmes/${encodedUri}`, {
      readme: content
    });
  }
  // =========================================================================
  // Tags API
  // =========================================================================
  /**
   * Get tags for a dataset
   */
  async getTags(uri) {
    const encodedUri = encodeURIComponent(uri);
    return this.request("GET", `/tags/${encodedUri}`);
  }
  /**
   * Set all tags for a dataset (replaces existing)
   */
  async setTags(uri, tags) {
    const encodedUri = encodeURIComponent(uri);
    return this.request("PUT", `/tags/${encodedUri}`, { tags });
  }
  /**
   * Add a single tag to a dataset
   */
  async addTag(uri, tag) {
    const encodedUri = encodeURIComponent(uri);
    const encodedTag = encodeURIComponent(tag);
    return this.request(
      "POST",
      `/tags/${encodedUri}/${encodedTag}`,
      {}
    );
  }
  /**
   * Remove a single tag from a dataset
   */
  async removeTag(uri, tag) {
    const encodedUri = encodeURIComponent(uri);
    const encodedTag = encodeURIComponent(tag);
    return this.request(
      "DELETE",
      `/tags/${encodedUri}/${encodedTag}`
    );
  }
  // =========================================================================
  // Annotations API
  // =========================================================================
  /**
   * Get annotations for a dataset
   */
  async getAnnotations(uri) {
    const encodedUri = encodeURIComponent(uri);
    return this.request(
      "GET",
      `/annotations/${encodedUri}`
    );
  }
  /**
   * Set all annotations for a dataset (replaces existing)
   */
  async setAnnotations(uri, annotations) {
    const encodedUri = encodeURIComponent(uri);
    return this.request(
      "PUT",
      `/annotations/${encodedUri}`,
      { annotations }
    );
  }
  /**
   * Set a single annotation
   */
  async setAnnotation(uri, name, value) {
    const encodedUri = encodeURIComponent(uri);
    const encodedName = encodeURIComponent(name);
    return this.request(
      "PUT",
      `/annotations/${encodedUri}/${encodedName}`,
      { value }
    );
  }
  /**
   * Delete a single annotation
   */
  async deleteAnnotation(uri, name) {
    const encodedUri = encodeURIComponent(uri);
    const encodedName = encodeURIComponent(name);
    return this.request(
      "DELETE",
      `/annotations/${encodedUri}/${encodedName}`
    );
  }
  // =========================================================================
  // User Management API (Admin only)
  // =========================================================================
  /**
   * Get current user info (includes is_admin flag)
   */
  async getCurrentUser() {
    return this.request("GET", "/me");
  }
  /**
   * List all users (admin only)
   */
  async listUsers() {
    return this.request("GET", "/users");
  }
  /**
   * Get a specific user
   */
  async getUser(username) {
    const encodedUsername = encodeURIComponent(username);
    return this.request("GET", `/users/${encodedUsername}`);
  }
  /**
   * Create or register a new user (admin only)
   * Note: dserver uses PUT for user creation/update (idempotent operation)
   */
  async createUser(username, options) {
    const encodedUsername = encodeURIComponent(username);
    return this.request("PUT", `/users/${encodedUsername}`, options || {});
  }
  /**
   * Delete a user (admin only)
   */
  async deleteUser(username) {
    const encodedUsername = encodeURIComponent(username);
    await this.request("DELETE", `/users/${encodedUsername}`);
  }
  /**
   * Update user fields (partial update, admin only)
   *
   * @param username - Username to update
   * @param updates - Fields to update (is_admin, display_name)
   * @returns Updated user info
   */
  async updateUser(username, updates) {
    const encodedUsername = encodeURIComponent(username);
    return this.request("PATCH", `/users/${encodedUsername}`, updates);
  }
  /**
   * Update user admin status (admin only)
   * @deprecated Use updateUser() instead for more flexibility
   */
  async updateUserAdmin(username, isAdmin) {
    return this.updateUser(username, { is_admin: isAdmin });
  }
  // =========================================================================
  // Base URI Management API (Admin only)
  // =========================================================================
  /**
   * List all base URIs (admin only)
   */
  async listBaseURIs() {
    return this.request("GET", "/base-uris");
  }
  /**
   * Get a specific base URI
   */
  async getBaseURI(baseUri) {
    const encodedUri = encodeURIComponent(baseUri);
    return this.request("GET", `/base-uris/${encodedUri}`);
  }
  /**
   * Register a new base URI (admin only)
   */
  async createBaseURI(baseUri) {
    const encodedUri = encodeURIComponent(baseUri);
    return this.request("POST", `/base-uris/${encodedUri}`, {});
  }
  /**
   * Delete a base URI (admin only)
   */
  async deleteBaseURI(baseUri) {
    const encodedUri = encodeURIComponent(baseUri);
    await this.request("DELETE", `/base-uris/${encodedUri}`);
  }
  /**
   * Grant search permission to a user on a base URI (admin only)
   */
  async grantSearchPermission(username, baseUri) {
    const encodedUsername = encodeURIComponent(username);
    const encodedUri = encodeURIComponent(baseUri);
    return this.request("POST", `/users/${encodedUsername}/search/${encodedUri}`, {});
  }
  /**
   * Revoke search permission from a user on a base URI (admin only)
   */
  async revokeSearchPermission(username, baseUri) {
    const encodedUsername = encodeURIComponent(username);
    const encodedUri = encodeURIComponent(baseUri);
    return this.request("DELETE", `/users/${encodedUsername}/search/${encodedUri}`);
  }
  /**
   * Grant register permission to a user on a base URI (admin only)
   */
  async grantRegisterPermission(username, baseUri) {
    const encodedUsername = encodeURIComponent(username);
    const encodedUri = encodeURIComponent(baseUri);
    return this.request("POST", `/users/${encodedUsername}/register/${encodedUri}`, {});
  }
  /**
   * Revoke register permission from a user on a base URI (admin only)
   */
  async revokeRegisterPermission(username, baseUri) {
    const encodedUsername = encodeURIComponent(username);
    const encodedUri = encodeURIComponent(baseUri);
    return this.request("DELETE", `/users/${encodedUsername}/register/${encodedUri}`);
  }
  // =========================================================================
  // UUID Lookup API
  // =========================================================================
  /**
   * Get datasets by UUID
   * Returns all instances of a dataset with that UUID across base URIs the user has access to
   *
   * @param uuid - Dataset UUID
   * @returns Array of datasets with that UUID
   */
  async getDatasetsByUuid(uuid) {
    return this.request(
      "GET",
      `/uuids/${encodeURIComponent(uuid)}`
    );
  }
  // =========================================================================
  // Dependency Graph Plugin API
  // =========================================================================
  /**
   * Get dependency graph for a dataset by UUID
   * Returns all datasets in the same dependency graph (bidirectional traversal)
   * Requires dserver-dependency-graph-plugin to be installed
   *
   * @param uuid - Dataset UUID
   * @param dependencyKeys - Optional array of custom dependency keys (e.g., ["readme.derived_from.uuid"])
   * @returns Array of datasets in the dependency graph with derived_from relationships
   */
  async getDependencyGraph(uuid, dependencyKeys) {
    const encodedUuid = encodeURIComponent(uuid);
    if (dependencyKeys && dependencyKeys.length > 0) {
      return this.request(
        "POST",
        `/graph/uuids/${encodedUuid}`,
        { dependency_keys: dependencyKeys }
      );
    }
    return this.request("GET", `/graph/uuids/${encodedUuid}`);
  }
};

// src/vue.ts
import { ref, computed, shallowRef } from "vue";
function useDServerClient(config) {
  const client = computed(() => {
    const cfg = "value" in config ? config.value : config;
    return new DServerClient(cfg);
  });
  return { client };
}
function useDatasetDownload(client) {
  const loading = ref(false);
  const error = shallowRef(null);
  const urls = shallowRef(null);
  const manifest = shallowRef(null);
  const adminMetadata = shallowRef(null);
  const readme = ref(null);
  const progress = ref({
    loaded: 0,
    total: 0
  });
  async function loadDataset(uri) {
    loading.value = true;
    error.value = null;
    try {
      urls.value = await client.value.getDatasetSignedUrls(uri);
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  }
  async function loadManifest() {
    if (!urls.value) {
      throw new Error("No dataset loaded. Call loadDataset first.");
    }
    loading.value = true;
    error.value = null;
    try {
      manifest.value = await client.value.downloadManifest(urls.value);
      return manifest.value;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  }
  async function loadAdminMetadata() {
    if (!urls.value) {
      throw new Error("No dataset loaded. Call loadDataset first.");
    }
    loading.value = true;
    error.value = null;
    try {
      adminMetadata.value = await client.value.downloadAdminMetadata(urls.value);
      return adminMetadata.value;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  }
  async function loadReadme() {
    if (!urls.value) {
      throw new Error("No dataset loaded. Call loadDataset first.");
    }
    loading.value = true;
    error.value = null;
    try {
      readme.value = await client.value.downloadReadme(urls.value);
      return readme.value;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  }
  async function downloadItem(identifier, options) {
    if (!urls.value) {
      throw new Error("No dataset loaded. Call loadDataset first.");
    }
    loading.value = true;
    error.value = null;
    progress.value = { loaded: 0, total: 0, item: identifier };
    try {
      const result = await client.value.downloadItem(urls.value, identifier, {
        ...options,
        onProgress: (loaded, total, item) => {
          progress.value = { loaded, total, item };
          options?.onProgress?.(loaded, total, item);
        }
      });
      return result;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  }
  async function downloadItemAsBlob(identifier, mimeType, options) {
    const content = await downloadItem(identifier, options);
    return new Blob([content], { type: mimeType });
  }
  async function downloadItemToFile(identifier, filename, mimeType, options) {
    const blob = await downloadItemAsBlob(identifier, mimeType, options);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  function getItemDetails(identifier) {
    if (!manifest.value) return null;
    return manifest.value.items[identifier] || null;
  }
  const items = computed(() => {
    if (!manifest.value) return [];
    return Object.entries(manifest.value.items).map(([id, item]) => ({
      identifier: id,
      ...item
    }));
  });
  return {
    // State
    loading,
    error,
    urls,
    manifest,
    adminMetadata,
    readme,
    progress,
    items,
    // Actions
    loadDataset,
    loadManifest,
    loadAdminMetadata,
    loadReadme,
    downloadItem,
    downloadItemAsBlob,
    downloadItemToFile,
    getItemDetails
  };
}
function useDatasetUpload(client) {
  const loading = ref(false);
  const error = shallowRef(null);
  const progress = ref({
    uploaded: 0,
    total: 0,
    phase: "preparing"
  });
  const result = shallowRef(null);
  async function uploadDataset(baseUri, name, files, options) {
    loading.value = true;
    error.value = null;
    result.value = null;
    progress.value = { uploaded: 0, total: 0, phase: "preparing" };
    try {
      progress.value.phase = "uploading";
      const uploadResult = await client.value.createDataset(
        baseUri,
        name,
        files,
        {
          ...options,
          onProgress: (uploaded, total, currentFile) => {
            progress.value = {
              uploaded,
              total,
              currentFile,
              phase: "uploading"
            };
            options?.onProgress?.(uploaded, total, currentFile);
          }
        }
      );
      progress.value.phase = "complete";
      result.value = uploadResult;
      return uploadResult;
    } catch (e) {
      progress.value.phase = "error";
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  }
  async function uploadFromFileList(baseUri, name, fileList, basePath = "", options) {
    const files = Array.from(fileList).map((file) => ({
      relpath: basePath ? `${basePath}/${file.name}` : file.name,
      content: file,
      contentType: file.type || void 0
    }));
    return uploadDataset(baseUri, name, files, options);
  }
  async function uploadFromDataTransfer(baseUri, name, dataTransfer, options) {
    const files = [];
    if (dataTransfer.items) {
      for (const item of Array.from(dataTransfer.items)) {
        if (item.kind === "file") {
          const entry = item.webkitGetAsEntry?.();
          if (entry) {
            await collectFiles(entry, "", files);
          } else {
            const file = item.getAsFile();
            if (file) {
              files.push({
                relpath: file.name,
                content: file,
                contentType: file.type || void 0
              });
            }
          }
        }
      }
    } else {
      for (const file of Array.from(dataTransfer.files)) {
        files.push({
          relpath: file.name,
          content: file,
          contentType: file.type || void 0
        });
      }
    }
    return uploadDataset(baseUri, name, files, options);
  }
  async function collectFiles(entry, basePath, files) {
    if (entry.isFile) {
      const fileEntry = entry;
      const file = await new Promise((resolve, reject) => {
        fileEntry.file(resolve, reject);
      });
      files.push({
        relpath: basePath ? `${basePath}/${file.name}` : file.name,
        content: file,
        contentType: file.type || void 0
      });
    } else if (entry.isDirectory) {
      const dirEntry = entry;
      const reader = dirEntry.createReader();
      const entries = await new Promise((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      const newBasePath = basePath ? `${basePath}/${entry.name}` : entry.name;
      for (const childEntry of entries) {
        await collectFiles(childEntry, newBasePath, files);
      }
    }
  }
  const progressPercent = computed(() => {
    if (progress.value.total === 0) return 0;
    return Math.round(progress.value.uploaded / progress.value.total * 100);
  });
  return {
    // State
    loading,
    error,
    progress,
    progressPercent,
    result,
    // Actions
    uploadDataset,
    uploadFromFileList,
    uploadFromDataTransfer
  };
}
export {
  AuthenticationError,
  AuthorizationError,
  DServerClient,
  DServerError,
  NotFoundError,
  chunk,
  delay,
  encodeUri,
  formatBytes,
  generateIdentifier,
  generateUUID,
  getCurrentTimestamp,
  isExpired,
  parallelLimit,
  parseISOTimestamp,
  parsePaginationHeader,
  timeUntilExpiry,
  useDServerClient,
  useDatasetDownload,
  useDatasetUpload,
  withRetry
};
